import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import Button from '../BaseComponents/Button';
import AddSupplementForm from './AddSupplementForm';
import TableRowActionMenu from '../BaseComponents/TableRowActionMenu';
import { getSupplementRegimesByMember, createSupplementRegime, unassignSupplementRegime } from '../../api/supplementRegimes';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import './Supplements.scss';

const Supplements: React.FC = () => {
  const [supplements, setSupplements] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [editingSupplement, setEditingSupplement] = useState<any | null>(null);
  
  const { memberData } = useSelector((state: RootState) => state.member);
  const assigned_doctor = JSON.parse(sessionStorage.getItem("assigned_doctor") ?? "false"); 
  const {admin: { role }} = useSelector((state: RootState) => state.admin);
  const visible = ((role !== "doctor") || (assigned_doctor && role === "doctor"));    
  const fetchSupplements = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (!memberData?.user_id) {
        throw new Error('Member ID not available');
      }
      
      const response = await getSupplementRegimesByMember(memberData.user_id);
      
      setSupplements(response);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch supplements');
    } finally {
      setIsLoading(false);
    }
  }, [memberData?.user_id]);

  useEffect(() => {
    if (memberData?.user_id) {
      fetchSupplements();
    }
  }, [memberData?.user_id, fetchSupplements]);

  const handleAddSupplement = () => {
    setIsAddFormOpen(true);
  };

  const handleCloseAddForm = () => {
    setIsAddFormOpen(false);
    setEditingSupplement(null);
  };

  const handleEdit = (regime: any) => {
    setEditingSupplement(regime);
    setIsAddFormOpen(true);
  };

  const handleSubmitSupplement = async (formData: any) => {
    try {
      setIsSubmitting(true);
      
      if (formData._isUpdate) {
        // Update case - toast is handled in AddSupplementForm
      } else {
        const apiPayload = {
          member_id: formData.member_id,
          supplement_id: formData.supplement_id,
          dosage: formData.dosage,
          dosage_units: formData.dosage_units,
          meal_relation: formData.meal_relation,
          time_of_day: formData.time_of_day,
          instructions_for_taking: formData.instructions_for_taking,
          additional_notes: formData.additional_notes
        };

        await createSupplementRegime(apiPayload);
        
      }
      
      await fetchSupplements();
      
      window.dispatchEvent(new Event('supplement-created'));

      handleCloseAddForm();
    } catch (error) {
      toast.error('Failed to save supplement. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefresh = () => {
    fetchSupplements();
  };

  const handleUnassign = async (regime: any) => {
    if (!memberData?.user_id) {
      toast.error('Member data not available');
      return;
    }

    try {
      await unassignSupplementRegime({
        regime_id: regime.id,
        member_id: memberData.user_id
      });
      
      toast.success('Supplement regime unassigned successfully');
      await fetchSupplements();
    } catch (error: any) {
      toast.error(error.message || 'Failed to unassign supplement regime');
    }
  };

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading supplements...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="error-state">
          <div className="error-text">{error}</div>
          <button 
            className="retry-button" 
            onClick={handleRefresh}
          >
            Retry
          </button>
        </div>
      );
    }

    if (supplements.length === 0) {
      return (
        <div className="empty-state">
          <div className="empty-text">No supplements available</div>
          <div className="empty-subtext">Click "Add Supplement" to get started</div>
        </div>
      );
    }

    return (
      <>
        <div className="table-header">
          <div className="header-cell">Supplement</div>
          <div className="header-cell">Dosage</div>
          <div className="header-cell">Meal Relation</div>
          <div className="header-cell">Time of Day</div>
          <div className="header-cell">Additional Notes</div>
          <div className="header-cell">Actions</div>
        </div>

        <div className="table-body">
          {supplements.map((regime, index) => (
            <div className="table-row" key={regime.id || index}>
              <div className="table-cell supplement-name">
                {regime.supplement_name || 'No Name'}
              </div>
              <div className="table-cell">
                {regime.dosage ? `${regime.dosage} ${regime.dosage_units || 'mg'}` : '-'}
              </div>
              <div className="table-cell">
                {regime.meal_relation || '-'}
              </div>
              <div className="table-cell">
                {regime.time_of_day && regime.time_of_day.length > 0 ? regime.time_of_day.join(', ') : '-'}
              </div>
              <div className="table-cell additional-notes">
                {regime.additional_notes || '-'}
              </div>          
              <div className="table-cell actions">
                {visible ? 
                <TableRowActionMenu
                  options={[
                    { key: 'edit', value: 'Edit' },
                    { key: 'unassign', value: 'Unassign' }
                  ]}
                  id={regime.id}
                  onActionMenuClick={(id, action) => {
                    if (action === 'edit') {
                      handleEdit(regime);
                    } else if (action === 'unassign') {
                      handleUnassign(regime);
                    }
                  }}
                /> : "Not Allowed"}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  if (!memberData?.user_id) {
    return (
      <div className="supplements-container">
        <div className="loading-state">
          <div>Loading member data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="supplements-container">
      <div className="supplements-header-row sideBarPad2">
        <div className="search-and-filter-container d-flex align-items-center gap-20">
          <div className="count-container">
            {supplements?.length || 0}{' '}
            {supplements?.length === 1 ? 'Supplement' : 'Supplements'}
          </div>
        </div>
        <div className="button-container">
          {visible && 
          <Button
            isSecondary={true}
            text="Add Supplement Regime"
            onClick={handleAddSupplement}
          />}
        </div>
      </div>

      <div className="supplements-table">
        {renderTableContent()}
      </div>

      <AddSupplementForm
        isOpen={isAddFormOpen}
        onClose={handleCloseAddForm}
        onSubmit={handleSubmitSupplement}
        isSubmitting={isSubmitting}
        editingSupplement={editingSupplement}
        memberId={memberData.user_id}
      />
    </div>
  );
};

export default Supplements;
